import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import logo from '../pages/images1/logo_L.png';

const LOGO_URL = logo;

const navLinks = [
  {
    name: 'Home',
    path: '/'
  },

  {
    name: 'About',
    path: '/about'
  },

  {
    name: 'Core Competencies',
    path: '/services',

    children: [

      {
        name: 'Commercial Biogas-CBG',
        path: '/services/biogas'
      },

      {
        name: 'RBHM™ Technology',
        path: '/services/rbhm'
      },

      {
        name: 'Smart O&M for Biogas & CBG Plants',
        path: '/services/smart-om-biogas'
      },

      {
        name: 'RRR™ Prototype Digester',
        path: '/services/RRRProto'
      },

      // NEW KVIC BIOGAS PLANTS PAGE
      {
        name: 'KVIC Biogas Plants',
        path: '/services/kvic-biogas'
      },

      {
        name: 'Biogas Lab',
        path: '/services/lab'
      },

      {
        name: 'Digestate Managment',
        path: '/services/dige'
      },

      {
        name: 'Composting Systems',
        path: '/services/composting'
      },

      {
        name: 'Biomass Briquettes',
        path: '/services/biomass'
      },

      {
        name: 'Water Solutions',
        path: '/services/water'
      },

    ]
  },

  {
    name: 'Gallery',
    path: '/gallery'
  },

  {
    name: 'Careers',
    path: '/careers'
  },

  {
    name: 'Contact',
    path: '/contact'
  },

];

export const Navbar = () => {

  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showNav, setShowNav] = useState(true);

  const location = useLocation();

  // Scroll background change
  useEffect(() => {

    const handleScroll = () => {

      setIsScrolled(window.scrollY > 50);

    };

    window.addEventListener('scroll', handleScroll);

    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);

  }, []);

  // Navbar show when mouse near top
  useEffect(() => {

    const handleMouseMove = (e) => {

      if (e.clientY < 80) {

        setShowNav(true);

      } else {

        setShowNav(false);

      }

    };

    window.addEventListener("mousemove", handleMouseMove);

    return () => window.removeEventListener("mousemove", handleMouseMove);

  }, []);

  // Scroll to top on route change
  useEffect(() => {

    window.scrollTo(0, 0);

    setIsOpen(false);

    setActiveDropdown(null);

  }, [location.pathname]);

  return (

    <motion.nav

      className={`fixed top-0 left-0 right-0 z-50 transform transition-all duration-500 ${
        showNav ? "translate-y-0" : "-translate-y-full"
      } bg-emerald-950 shadow-lg`}

      initial={{ y: -100 }}

      animate={{ y: 0 }}

      transition={{ duration: 0.5 }}

    >

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="flex items-center justify-between h-20">

          {/* Logo */}

          <Link to="/" className="flex-shrink-0">

            <img
              src={LOGO_URL}
              alt="RENVITEK"
              className="h-12 md:h-14"
            />

          </Link>

          {/* Desktop Navigation */}

          <div className="hidden lg:flex items-center gap-x-8">

            {navLinks.map((link) => (

              <div
                key={link.name}
                className="relative"
              >

                {link.children ? (

                  <div

                    onMouseEnter={() => setActiveDropdown(link.name)}

                    onMouseLeave={() => setActiveDropdown(null)}

                  >

                    <button

                      className={`text-white flex items-center gap-1 py-2 text-sm uppercase tracking-wider font-medium ${
                        location.pathname.startsWith('/services')
                          ? 'text-amber-400'
                          : ''
                      }`}

                    >

                      {link.name}

                      <ChevronDown size={16}/>

                    </button>

                    <AnimatePresence>

                      {activeDropdown === link.name && (

                        <motion.div

                          initial={{
                            opacity: 0,
                            y: 10
                          }}

                          animate={{
                            opacity: 1,
                            y: 0
                          }}

                          exit={{
                            opacity: 0,
                            y: 10
                          }}

                          className="absolute top-full left-0 mt-2 w-64 bg-emerald-900 shadow-xl py-2"

                        >

                          {link.children.map((child)=>(

                            <Link

                              key={child.name}

                              to={child.path}

                              className="block px-4 py-3 text-white/80 hover:text-amber-400 hover:bg-emerald-800 text-sm transition-colors"

                            >

                              {child.name}

                            </Link>

                          ))}

                        </motion.div>

                      )}

                    </AnimatePresence>

                  </div>

                ) : (

                  <Link

                    to={link.path}

                    className={`text-white py-2 text-sm uppercase tracking-wider font-medium ${
                      location.pathname === link.path
                        ? 'text-amber-400'
                        : ''
                    }`}

                  >

                    {link.name}

                  </Link>

                )}

              </div>

            ))}

          </div>

          {/* Mobile Menu Button */}

          <button

            onClick={()=>setIsOpen(!isOpen)}

            className="lg:hidden text-white p-2"

          >

            {isOpen ? <X size={28}/> : <Menu size={28}/>}

          </button>

        </div>

      </div>

      {/* Mobile Menu */}

      <AnimatePresence>

        {isOpen && (

          <>

            <motion.div

              className="fixed inset-0 bg-black/80 z-[100] lg:hidden"

              initial={{
                opacity: 0
              }}

              animate={{
                opacity: 1
              }}

              exit={{
                opacity: 0
              }}

              onClick={()=>setIsOpen(false)}

            />

            <motion.div

              className="fixed top-0 right-0 w-[85%] max-w-sm h-screen z-[101] p-6 lg:hidden shadow-2xl overflow-y-auto"

              style={{
                backgroundColor:'#022c22'
              }}

              initial={{
                x:'100%'
              }}

              animate={{
                x:0
              }}

              exit={{
                x:'100%'
              }}

              transition={{
                duration:0.3
              }}

            >

              <button

                onClick={()=>setIsOpen(false)}

                className="absolute top-6 right-6 text-white"

              >

                <X size={28}/>

              </button>

              <div className="mb-8">

                <img
                  src={LOGO_URL}
                  alt="RENVITEK"
                  className="h-10"
                />

              </div>

              <div className="space-y-4">

                {navLinks.map((link)=>(

                  <div key={link.name}>

                    {link.children ? (

                      <>

                        <button

                          onClick={()=>setActiveDropdown(
                            activeDropdown === link.name
                              ? null
                              : link.name
                          )}

                          className="w-full flex items-center justify-between text-white py-3 text-lg"

                        >

                          {link.name}

                          <ChevronDown/>

                        </button>

                        {activeDropdown === link.name && (

                          <div className="pl-4 border-l border-emerald-700">

                            {link.children.map((child)=>(

                              <Link

                                key={child.name}

                                to={child.path}

                                className="block py-2 text-white/70"

                                onClick={()=>setIsOpen(false)}

                              >

                                {child.name}

                              </Link>

                            ))}

                          </div>

                        )}

                      </>

                    ) : (

                      <Link

                        to={link.path}

                        className="block py-3 text-lg text-white"

                        onClick={()=>setIsOpen(false)}

                      >

                        {link.name}

                      </Link>

                    )}

                  </div>

                ))}

              </div>

            </motion.div>

          </>

        )}

      </AnimatePresence>

    </motion.nav>

  );

};

export default Navbar;
